import genericStyles from "../../Styles/genericStyles.module.css";
import MUIDataTableCustomStyle from "../../Components/MUIDataTableCustomStyle";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useState, createContext, useEffect, useMemo } from "react";
import { localUrlEnum, endpoints } from "../../Utils/UrlEnum";
import { OrderModel } from "../../Utils/Models";
import { Box, Chip } from "@mui/material";
import Config from "../../Utils/Config";
import { EComponentTypes, ETableEditAction } from "../../Utils/Enums";
import {
  fetchProducts,
  getFullName,
  setFontColorBasedOnBackground,
} from "../../Utils/Utils";
import moment from "moment";
import { TablesIds } from "../../Utils/Constants";
import Filters from "../Widgets/Filters";
import { Filter } from "../../Utils/Types";
import * as yup from "yup";
import useCustomMemo from "../../Hooks/useCustomMemo";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import withRole from "../../Hooks/withRole";
import { getFromCacheOrFetch } from "../../Utils/getFromCacheOrFetch";

export const CRUDContext = createContext(new OrderModel());

function OrdersTable(props: any) {
  const [contextValue, setContextValue] = useState(new OrderModel());

  const memo = useCustomMemo();
  let orderStatuses = memo?.storage.orderStatuses || [];

  // Filter out excluded statuses for instructors
  if (props?.excludedOrderStatuses && props.excludedOrderStatuses.length > 0) {
    orderStatuses = orderStatuses.filter(
      (status: any) => !props.excludedOrderStatuses.includes(status.name),
    );
  }

  const workPointId = localStorage.getItem("userWorkPointId");
  const queryParams = props?.restrictByWorkPoint
    ? "/paginated?workPointId=" + workPointId
    : "";

  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    let mounted = true;

    getFromCacheOrFetch<any[]>({
      cacheKey: "products",
      storage: memo.storage,
      setStorage: memo.setStorageData,
      fetcher: fetchProducts(),
      isValid: (data) => Array.isArray(data) && data.length > 0,
    }).then((data) => {
      if (mounted) {
        setProducts(data);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  const filters = useMemo<Filter[]>(
    () => [
      {
        property: "status",
        label: Vocabulary.orderStatus,
        type: EComponentTypes.AUTOCOMPLETE,
        validation: yup.mixed().nullable(),
        defaultValue: null,
        options: orderStatuses,
        autocompleteFilterProperty: "_id",
        customGetOptionLabel: (option: any) => {
          if (!option?.name) return "";
          const typeName = option?.typeId?.name;
          return typeName ? `${option.name} (${typeName})` : option.name;
        },
      },
      {
        property: "productId",
        label: Vocabulary.service,
        type: EComponentTypes.AUTOCOMPLETE,
        validation: yup.mixed().nullable(),
        defaultValue: null,
        options: products,
        autocompleteFilterProperty: "_id",
      },
    ],
    [orderStatuses, products],
  );

  /**
   *
   */
  const ordersColumns = [
    {
      label: ` `,
      name: "_id",
      options: {
        sort: false,
        display: false,
      },
    },
    {
      label: `${Vocabulary.status}`,
      name: "status",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
          style: { whiteSpace: "nowrap" },
        }),
        setCellProps: () => ({
          align: "center",
          style: { padding: "4px 4px" },
        }),
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: (rowData: any) => {
          return (
            <Box sx={{ maxWidth: { xs: "70px", sm: "none" } }}>
              <Chip
                color="primary"
                key={rowData?._id}
                label={rowData?.name}
                style={{
                  margin: 2,
                  backgroundColor: rowData?.color,
                  color: setFontColorBasedOnBackground(rowData?.color),
                  maxWidth: "100%",
                }}
                sx={{
                  fontSize: { xs: "0.7rem" },
                  height: { xs: "auto", sm: "32px" },
                  "& .MuiChip-label": {
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                    overflowWrap: { xs: "break-word", sm: "normal" },
                    overflow: { xs: "visible", sm: "hidden" },
                    textOverflow: { xs: "unset", sm: "ellipsis" },
                    padding: { xs: "2px 6px", sm: "0 12px" },
                  },
                }}
              />
            </Box>
          );
        },
        exportValue: (rowData: any) => rowData?.name,
      },
    },

    ...(!props?.showDiffColumns
      ? [
          {
            label: `${Vocabulary.instructor}`,
            name: "instructor_id",
            options: {
              sort: false,
              customBodyRender: (rowData: any) => {
                return getFullName(rowData?.user_id);
              },
              exportValue: (rowData: any) => getFullName(rowData?.user_id),
            },
          },
        ]
      : []),
    ...(!props?.showDiffColumns
      ? [
          {
            label: `${Vocabulary.CNP}`,
            name: "client_id",
            options: {
              sort: false,
              customBodyRender: (rowData: any) => {
                return rowData?.CNP;
              },
              exportValue: (rowData: any) => rowData?.CNP || "",
            },
          },
        ]
      : []),
    {
      label: `${Vocabulary.clientName}`,
      name: "client_id",
      options: {
        sort: false,
        customBodyRender: (rowData: any) => {
          return (
            <>
              <div>
                {`${rowData?.lastName} ${rowData?.firstName} ${
                  !rowData?.lastName && !rowData?.firstName ? "-" : ""
                }
                  `}
              </div>
            </>
          );
        },
        exportValue: (rowData: any) => {
          const fullName = `${rowData?.lastName} ${rowData?.firstName}`.trim();
          return fullName || "";
        },
      },
    },
    {
      label: `${Vocabulary.phone}`,
      name: "client_id",
      options: {
        sort: false,
        customBodyRender: (client: any) => {
          return (
            <>
              {client ? (
                <a
                  key={client.phone}
                  href={`tel:${client.phone}`}
                  style={{ textDecoration: "none" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Chip
                    icon={<LocalPhoneIcon fontSize="small" />}
                    //color="primary"
                    label={client.phone}
                    style={{ padding: 5 }}
                  />
                </a>
              ) : null}
            </>
          );
        },
        exportValue: (client: any) => client?.phone || "",
      },
    },

    {
      label: `${Vocabulary.service}`,
      name: "product",
      options: {
        sort: false,
        customBodyRender: (rowData: any) => {
          return (
            <>
              <div>
                {`${rowData?.name}
                  `}
              </div>
            </>
          );
        },
        exportValue: (rowData: any) => rowData?.name || "",
      },
    },

    ...(!props?.showDiffColumns
      ? [
          {
            label: `${Vocabulary.workPoint}`,
            name: "client_id",
            options: {
              sort: false,
              customBodyRender: (rowData: any) => {
                return rowData?.workPointId?.name;
              },
              exportValue: (rowData: any) => rowData?.workPointId?.name || "",
            },
          },
        ]
      : []),
    {
      label: `${Vocabulary.date}`,
      name: "createdAt",
      options: {
        sort: false,
        customBodyRender: (rowData: any) => {
          return moment(rowData).format(Config.momentEUDateFormat);
        },
        exportValue: (rowData: any) =>
          moment(rowData).format(Config.momentEUDateFormat),
      },
    },
  ];

  return (
    <div className={genericStyles.mainContainer}>
      <Filters filters={filters} defaultOpen />
      <CRUDContext.Provider value={contextValue}>
        <MUIDataTableCustomStyle
          onEditAction={ETableEditAction.REDIRECT}
          title={Vocabulary.cursanti}
          columns={ordersColumns}
          CRUDEndPoint={endpoints.ordersEndpoint + queryParams}
          id={TablesIds.orders}
          setContextValue={setContextValue}
          rowClickURL={localUrlEnum.newOrder}
          onRowClick={(rowData: any) => {
            setContextValue(rowData);
            const link = document.createElement("a");
            link.href = `${localUrlEnum.order}/${rowData._id}`;
            link.target = "_blank";
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          editURL={`${localUrlEnum.order}`}
          selectableRows={"none"}
          hideAddButton={props?.hideAddButton}
          hideDeleteButton={!props?.showDeleteButton}
          showDownloadButton={props?.showDownloadButton}
          showPrintButton={props?.showPrintButton}
          disableVerticalMobile
        />
      </CRUDContext.Provider>
    </div>
  );
}

export default withRole(OrdersTable);
